import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
    User, Building2, Mail, Phone, Globe, Linkedin, Calendar, MapPin,
    AlignVerticalJustifyStart,
    PanelLeftClose, PanelRightClose, RectangleHorizontal,
    Check, ChevronDown, ChevronUp, GripVertical, Eye, Search, X, Info,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import PronounsPicker from './PronounsPicker';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { resolveSigVars } from '../lib/signatureMergeVars';
import { signatureAssetMergeMap, API_BASE, rebaseSignatureAssetUrls } from '../lib/signatureAssets';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_DEFS = [
    { key: 'name', label: 'Full Name', icon: User, mergeVar: '{{SenderName}}', placeholder: 'John Smith', defaultEnabled: true },
    { key: 'title', label: 'Title', icon: Building2, mergeVar: '{{SenderTitle}}', placeholder: 'Sales Manager', defaultEnabled: true },
    { key: 'company', label: 'Company', icon: Building2, mergeVar: '{{SenderCompany}}', placeholder: 'Acme Inc.', defaultEnabled: true },
    { key: 'email', label: 'Email', icon: Mail, mergeVar: '{{SenderEmail}}', placeholder: 'john@acme.com', defaultEnabled: true },
    { key: 'phone', label: 'Phone', icon: Phone, mergeVar: '{{SenderPhone}}', placeholder: '+1 555 123 4567', defaultEnabled: true },
    { key: 'website', label: 'Website', icon: Globe, mergeVar: null, placeholder: 'acme.com', defaultEnabled: false },
    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, mergeVar: '{{SenderLinkedIn}}', placeholder: 'linkedin.com/in/johnsmith', defaultEnabled: false },
    { key: 'calendar_link', label: 'Booking Link', icon: Calendar, mergeVar: null, placeholder: 'cal.com/john', defaultEnabled: false },
    { key: 'address', label: 'Address', icon: MapPin, mergeVar: '{{SenderAddress}}', placeholder: '123 Main St, City', defaultEnabled: false },
    { key: 'pronouns', label: 'Pronouns', icon: User, mergeVar: null, placeholder: 'he/him', defaultEnabled: false },
    { key: 'tagline', label: 'Tagline', icon: Building2, mergeVar: null, placeholder: 'Helping teams sell smarter', defaultEnabled: false },
];

const SIGN_OFF_OPTIONS = [
    'Best regards,',
    'Kind regards,',
    'Regards,',
    'Warm regards,',
    'Best,',
    'Thanks,',
    'Thank you,',
    'Cheers,',
    'All the best,',
    'Sincerely,',
];

const DEFAULT_FIELD_ORDER = FIELD_DEFS.map(f => f.key);

const LAYOUTS = [
    { id: 'photo-left', label: 'Photo Left', icon: PanelLeftClose },
    { id: 'photo-right', label: 'Photo Right', icon: PanelRightClose },
    { id: 'stacked', label: 'Stacked', icon: AlignVerticalJustifyStart },
    { id: 'banner', label: 'Banner', icon: RectangleHorizontal },
];

const PHOTO_SHAPES = [
    { id: 'circle', label: 'Circle' },
    { id: 'rounded', label: 'Rounded' },
    { id: 'square', label: 'Square' },
];

const PRESETS = [
    { id: 'professional', label: 'Professional', accent: '#1c1917', text: '#44403c', divider: '#e7e5e4', nameSize: '16' },
    { id: 'modern', label: 'Modern', accent: '#2563eb', text: '#475569', divider: '#3b82f6', nameSize: '18' },
    { id: 'warm', label: 'Warm', accent: '#b45309', text: '#44403c', divider: '#f59e0b', nameSize: '16' },
    { id: 'minimal', label: 'Minimal', accent: '#525252', text: '#737373', divider: '#e5e5e5', nameSize: '14' },
    { id: 'bold', label: 'Bold', accent: '#dc2626', text: '#1c1917', divider: '#dc2626', nameSize: '20' },
];

const FONTS = [
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
    { value: "Georgia, 'Times New Roman', serif", label: 'Georgia' },
    { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
    { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
    { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
];

// Number ranges for the size inputs (#970). Values outside them are flagged in
// the form and clamped in the HTML, so a half-typed value never breaks the CSS.
export const SIZE_LIMITS = {
    name_size: { min: 12, max: 28, fallback: 16 },
    photo_size: { min: 40, max: 150, fallback: 80 },
    logo_size: { min: 16, max: 80, fallback: 32 },
};

const IMAGE_MERGE_VARS = ['{{SenderPhoto}}', '{{CompanyLogo}}'];
const HTTP_URL_RE = /^https?:\/\/\S+$/i;
// A link typed without a scheme ("cdn.acme.com/logo.png"): a dotted host, then an optional path.
const SCHEMELESS_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i;

/**
 * Photo and logo fields take links only (#970). Returns the link as it goes into
 * the HTML, or null when it is not a usable image link:
 * - an http(s) URL, as typed;
 * - {{SenderPhoto}} / {{CompanyLogo}} (no other merge variable is an image);
 * - a scheme-less "host/path", given https:// (the old free-text fields stored these);
 * - a same-origin "/api/…" path, made absolute so it works in the recipient's inbox.
 * `javascript:`, `data:` and anything else is rejected. Empty means "no image" ('').
 */
export function normalizeImageLink(value) {
    const v = (value || '').trim();
    if (!v) return '';
    if (HTTP_URL_RE.test(v) || IMAGE_MERGE_VARS.includes(v)) return v;
    if (/^\/api\/\S+$/.test(v)) return `${API_BASE}${v}`;
    if (SCHEMELESS_RE.test(v)) return `https://${v}`;
    return null;
}

export function isValidImageLink(value) {
    return normalizeImageLink(value) !== null;
}

function sizeInRange(key, value) {
    const { min, max } = SIZE_LIMITS[key];
    const s = String(value ?? '').trim();
    const n = Number(s);
    return s !== '' && Number.isFinite(n) && n >= min && n <= max;
}

function clampSize(key, value) {
    const { min, max, fallback } = SIZE_LIMITS[key];
    const n = Number(value);
    if (String(value ?? '').trim() === '' || !Number.isFinite(n)) return fallback;
    return Math.round(Math.max(min, Math.min(max, n)));
}

// The proxy URL a literal (non-merge-field) signature stores for "use my photo /
// logo" (#970): permanent, unlike the 1-hour presigned `user.picture`.
function senderAssetUrl(user, key) {
    return signatureAssetMergeMap(user)[key] || '';
}

// -------------------------------------------------------------------
// Email-safe HTML builder (client-side, used for preview + export)
// Uses HTML tables + inline styles for maximum Gmail/Outlook compat
// -------------------------------------------------------------------
function esc(t) { return (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

function buildSignatureHtml({ layout, fields, enabledFields, fieldOrder, style, photoUrl, logoUrl, photoShape, signOff }) {
    const accent = style.accent_color || '#1c1917';
    const textColor = style.text_color || '#44403c';
    const nameSize = clampSize('name_size', style.name_size);
    const font = style.font_family || 'Arial, Helvetica, sans-serif';
    const dividerColor = style.divider_color || '#e7e5e4';
    const photoSize = clampSize('photo_size', style.photo_size);
    // Same box as the server builder (`_logo_box`): height from logo_size, width at the old 120:32 ratio.
    const logoHeight = clampSize('logo_size', style.logo_size);
    const logoWidth = Math.round(logoHeight * 120 / 32);

    // Build name line
    const name = enabledFields.name ? esc(fields.name || '') : '';
    const pronouns = enabledFields.pronouns ? esc(fields.pronouns || '') : '';
    const title = enabledFields.title ? esc(fields.title || '') : '';
    const company = enabledFields.company ? esc(fields.company || '') : '';
    const tagline = enabledFields.tagline ? esc(fields.tagline || '') : '';
    const email = enabledFields.email ? esc(fields.email || '') : '';
    const phone = enabledFields.phone ? esc(fields.phone || '') : '';
    const website = enabledFields.website ? esc(fields.website || '') : '';
    const linkedin = enabledFields.linkedin ? esc(fields.linkedin || '') : '';
    const calLink = enabledFields.calendar_link ? esc(fields.calendar_link || '') : '';
    const address = enabledFields.address ? esc(fields.address || '') : '';

    // Name row
    let nameHtml = '';
    if (name) {
        nameHtml = `<tr><td style="padding:0;font-size:${nameSize}px;font-weight:700;color:${accent};font-family:${font};line-height:1.3">${name}`;
        if (pronouns) nameHtml += ` <span style="font-size:11px;color:#a8a29e;font-weight:400">(${pronouns})</span>`;
        nameHtml += '</td></tr>';
    }

    // Title row
    const titleHtml = title
        ? `<tr><td style="padding:2px 0 0 0;font-size:13px;color:${textColor};font-family:${font};line-height:1.4">${title}</td></tr>`
        : '';

    // Company row
    const companyHtml = company
        ? `<tr><td style="padding:1px 0 0 0;font-size:13px;color:${textColor};font-family:${font};line-height:1.4">${company}</td></tr>`
        : '';

    // Tagline row
    const taglineHtml = tagline
        ? `<tr><td style="padding:2px 0 0 0;font-size:12px;color:#78716c;font-style:italic;font-family:${font};line-height:1.4">${tagline}</td></tr>`
        : '';

    // Divider
    const divider = `<tr><td style="padding:8px 0"><table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="border-top:2px solid ${dividerColor};font-size:1px;line-height:1px" height="1">&nbsp;</td></tr></table></td></tr>`;

    // Contact info rows (each on its own line for Outlook)
    const contactRows = [];
    if (email) contactRows.push(`<a href="mailto:${email}" style="color:${accent};text-decoration:none;font-family:${font}">${email}</a>`);
    if (phone) contactRows.push(`<a href="tel:${phone}" style="color:${textColor};text-decoration:none;font-family:${font}">${phone}</a>`);
    if (website) {
        const href = website.startsWith('http') ? website : `https://${website}`;
        contactRows.push(`<a href="${esc(href)}" style="color:${accent};text-decoration:none;font-family:${font}" target="_blank">${website}</a>`);
    }
    const contactHtml = contactRows.length
        ? `<tr><td style="padding:2px 0 0 0;font-size:12px;line-height:1.8;font-family:${font}">${contactRows.join(' &#8226; ')}</td></tr>`
        : '';

    // Social links
    const socialParts = [];
    if (linkedin) {
        const href = linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`;
        socialParts.push(`<a href="${esc(href)}" style="color:${accent};text-decoration:none;font-size:12px;font-family:${font}" target="_blank">LinkedIn</a>`);
    }
    if (calLink) {
        const href = calLink.startsWith('http') ? calLink : `https://${calLink}`;
        socialParts.push(`<a href="${esc(href)}" style="color:${accent};text-decoration:none;font-size:12px;font-family:${font}" target="_blank">Book a Meeting</a>`);
    }
    const socialHtml = socialParts.length
        ? `<tr><td style="padding:4px 0 0 0;font-size:12px;font-family:${font}">${socialParts.join(' &middot; ')}</td></tr>`
        : '';

    // Address
    const addressHtml = address
        ? `<tr><td style="padding:4px 0 0 0;font-size:11px;color:#a8a29e;font-family:${font}">${address}</td></tr>`
        : '';

    // Logo
    const logoRow = logoUrl
        ? `<tr><td style="padding:8px 0 0 0"><img src="${esc(logoUrl)}" alt="${esc(company)}" style="max-height:${logoHeight}px;max-width:${logoWidth}px;display:block" width="${logoWidth}" /></td></tr>`
        : '';

    // Text block table
    const textTable = `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="font-family:${font}">${nameHtml}${titleHtml}${companyHtml}${taglineHtml}${(nameHtml || titleHtml || companyHtml) && (contactHtml || socialHtml) ? divider : ''}${contactHtml}${socialHtml}${addressHtml}${logoRow}</table>`;

    // Sign-off block (above signature)
    const signOffHtml = signOff
        ? `<p style="font-size:14px;color:${textColor};font-family:${font};margin:0 0 12px 0;line-height:1.4">${esc(signOff)}</p>`
        : '';

    // Photo cell
    const borderRadius = photoShape === 'circle' ? '50%' : (photoShape === 'rounded' ? '8px' : '0');
    const photoImg = photoUrl
        ? `<img src="${esc(photoUrl)}" alt="${esc(name)}" width="${photoSize}" height="${photoSize}" style="border-radius:${borderRadius};display:block;object-fit:cover" />`
        : '';

    // Wrap in outer table with Outlook-compatible structure
    // Using <!--[if mso]> conditional comments for Outlook width fix
    let inner;
    if ((layout === 'photo-left' || layout === 'photo-right') && photoImg) {
        const photoTd = `<td style="vertical-align:top;padding:0;width:${photoSize}px" width="${photoSize}" valign="top">${photoImg}</td>`;
        const spacerTd = `<td style="width:16px;font-size:1px" width="16">&nbsp;</td>`;
        const textTd = `<td style="vertical-align:top;padding:0" valign="top">${textTable}</td>`;
        const cells = layout === 'photo-left'
            ? `${photoTd}${spacerTd}${textTd}`
            : `${textTd}${spacerTd}${photoTd}`;
        inner = `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr>${cells}</tr></table>`;
    } else if (layout === 'banner' && photoImg) {
        inner = `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr><td style="padding:0 0 12px 0;text-align:center" align="center"><img src="${esc(photoUrl)}" alt="${esc(name)}" width="500" style="border-radius:8px;max-width:100%;height:auto;display:block" /></td></tr><tr><td style="text-align:center" align="center">${textTable}</td></tr></table>`;
    } else {
        const photoRow = photoImg ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr><td style="padding:0 0 10px 0">${photoImg}</td></tr></table>` : '';
        inner = `${photoRow}${textTable}`;
    }

    // Outer wrapper — max-width via Outlook conditional comment
    return `<!--[if mso]><table cellpadding="0" cellspacing="0" border="0" width="500"><tr><td><![endif]--><div style="max-width:500px;font-family:${font}">${signOffHtml}${inner}</div><!--[if mso]></td></tr></table><![endif]-->`;
}

/** Blank sender fields replaced by their merge variable (#970). */
function withSenderTokens(fields) {
    const out = { ...fields };
    FIELD_DEFS.forEach(def => {
        if (def.mergeVar && !String(out[def.key] ?? '').trim()) out[def.key] = def.mergeVar;
    });
    return out;
}

function buildSignatureText({ fields, enabledFields, signOff }) {
    const lines = [];
    if (signOff) { lines.push(signOff); lines.push(''); }
    const name = enabledFields.name ? fields.name : '';
    const pronouns = enabledFields.pronouns ? fields.pronouns : '';
    const title = enabledFields.title ? fields.title : '';
    const company = enabledFields.company ? fields.company : '';
    const tagline = enabledFields.tagline ? fields.tagline : '';
    const email = enabledFields.email ? fields.email : '';
    const phone = enabledFields.phone ? fields.phone : '';
    const website = enabledFields.website ? fields.website : '';
    const linkedin = enabledFields.linkedin ? fields.linkedin : '';
    const calLink = enabledFields.calendar_link ? fields.calendar_link : '';
    const address = enabledFields.address ? fields.address : '';

    if (name) lines.push(name + (pronouns ? ` (${pronouns})` : ''));
    if (title) lines.push(title);
    if (company) lines.push(company);
    if (tagline) lines.push(tagline);
    if (lines.length && (email || phone || website)) lines.push('---');
    if (email) lines.push(email);
    if (phone) lines.push(phone);
    if (website) lines.push(website);
    const social = [];
    if (linkedin) social.push(`LinkedIn: ${linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`}`);
    if (calLink) social.push(`Book a meeting: ${calLink.startsWith('http') ? calLink : `https://${calLink}`}`);
    social.forEach(s => lines.push(s));
    if (address) lines.push(address);
    return lines.join('\n');
}

// -------------------------------------------------------------------
// Searchable User Preview Dropdown
// -------------------------------------------------------------------
function UserPreviewSearch({ users, selectedId, selectedUser, onSelect }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        function handleClick(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Focus input when opened
    useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

    const filtered = useMemo(() => {
        if (!search.trim()) return users;
        const q = search.toLowerCase();
        return users.filter(u => {
            const name = (u.name || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            return name.includes(q) || email.includes(q);
        });
    }, [users, search]);

    const displayLabel = selectedUser ? (selectedUser.name || selectedUser.email) : 'Template view';

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                onClick={() => { setOpen(!open); setSearch(''); }}
                aria-label="Preview as"
                className="flex h-8 max-w-[220px] items-center gap-1.5 truncate rounded-md border border-input bg-background pl-7 pr-2 text-xs shadow-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
                <span className="truncate">{displayLabel}</span>
                <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
            </button>
            <Eye className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />

            {open && (
                <div className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-md border bg-popover shadow-md">
                    <div className="border-b p-2">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or email..."
                                aria-label="Search users"
                                className="h-8 w-full rounded-md border border-input bg-transparent pl-7 pr-7 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            />
                            {search && (
                                <button type="button" onClick={() => setSearch('')} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <X className="h-3 w-3 text-muted-foreground" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-1">
                        <button
                            type="button"
                            onClick={() => { onSelect(''); setOpen(false); }}
                            className={cn('flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-accent', !selectedId && 'font-medium')}
                        >
                            {!selectedId ? <Check className="h-3 w-3" /> : <span className="w-3" />}
                            Template view
                        </button>
                        {filtered.length === 0 && (
                            <p className="px-3 py-3 text-center text-xs text-muted-foreground">No users found</p>
                        )}
                        {filtered.map(u => {
                            const isSelected = u.user_id === selectedId;
                            return (
                                <button
                                    key={u.user_id}
                                    type="button"
                                    onClick={() => { onSelect(u.user_id); setOpen(false); }}
                                    className={cn('flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-accent', isSelected && 'font-medium')}
                                >
                                    {isSelected ? <Check className="h-3 w-3" /> : <span className="w-3" />}
                                    <span className="truncate">{u.name || u.email}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// -------------------------------------------------------------------
// Main Component
// -------------------------------------------------------------------
// The signature builder body (#970): tabs + live preview. It is rendered inside
// `signatures/SignatureBuilderDialog`, which owns the header (Visual / HTML /
// Text), the General tab content and the footer; both hosts (Admin → Signatures
// and Settings → Templates & Signatures) use that dialog.
//
// `mergeFields` (#955): build a personal signature from {{Sender*}} merge fields,
// the way admin templates are built, instead of typing the author's own details
// in. Composers resolve those fields against whoever SENDS, so a signature that
// is shared shows each sender their own name, title and phone. The preview is
// resolved against the signed-in user so they still see what they will send.
//
// `viewMode` comes from the dialog header (default 'visual'); `tab` is controlled
// when the parent passes it, else internal.
// `generalTab` ({ content, hasError }) adds a leading "General" tab.
// `onValidityChange({ style, photos })` reports which tabs hold invalid values.
export default function SignatureEditor({
    value, onChange, signatureData: initData, onDataChange,
    isAdmin = false, mergeFields = false,
    viewMode: viewModeProp,
    tab: tabProp, onTabChange,
    generalTab = null,
    onValidityChange,
}) {
    const { user, getAuthHeader } = useAuth();
    const useTokens = isAdmin || mergeFields;

    const [layout, setLayout] = useState('photo-left');
    const [photoShape, setPhotoShape] = useState('circle');
    const [fieldOrder, setFieldOrder] = useState(DEFAULT_FIELD_ORDER);
    const [enabledFields, setEnabledFields] = useState(() => {
        const defaults = {};
        FIELD_DEFS.forEach(f => { defaults[f.key] = f.defaultEnabled; });
        return defaults;
    });
    const [fields, setFields] = useState({
        name: '', title: '', company: '', email: '', phone: '',
        website: '', linkedin: '', address: '', pronouns: '',
        calendar_link: '', tagline: '',
    });
    const [style, setStyle] = useState({
        accent_color: '#1c1917', text_color: '#44403c', divider_color: '#e7e5e4',
        name_size: '16', font_family: 'Arial, Helvetica, sans-serif', photo_size: '80',
        logo_size: '32',
    });
    const [signOff, setSignOff] = useState('Best regards,');
    const [customSignOff, setCustomSignOff] = useState(false);
    const [photoUrl, setPhotoUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [useProfilePhoto, setUseProfilePhoto] = useState(false);
    const [useCompanyLogo, setUseCompanyLogo] = useState(false);

    // 'visual' | 'html' | 'text'; the dialog header owns the switch.
    const viewMode = viewModeProp ?? 'visual';

    const [tabState, setTabState] = useState(generalTab ? 'general' : 'style');
    const tab = tabProp ?? tabState;
    const setTab = (t) => { setTabState(t); onTabChange?.(t); };

    // Preview user selection (admin mode)
    const [previewUsers, setPreviewUsers] = useState([]);
    const [previewUserId, setPreviewUserId] = useState('');
    const [previewUser, setPreviewUser] = useState(null);
    // Fetch org users for preview selector (admin mode only)
    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const res = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() });
                const users = res.data?.users || res.data || [];
                users.sort((a, b) => {
                    const nameA = (a.name || a.email || '').toLowerCase();
                    const nameB = (b.name || b.email || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                setPreviewUsers(users);
            } catch { /* ignore */ }
        })();
    }, [isAdmin, getAuthHeader]);

    // DnD (pointer + keyboard)
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // Initialize from existing data
    useEffect(() => {
        if (initData && Object.keys(initData).length > 0) {
            if (initData.layout) setLayout(initData.layout);
            if (initData.photo_shape) setPhotoShape(initData.photo_shape);
            if (initData.field_order) setFieldOrder(initData.field_order);
            if (initData.fields) {
                setFields(prev => ({ ...prev, ...initData.fields }));
                if (initData.enabled_fields) {
                    setEnabledFields(initData.enabled_fields);
                } else {
                    const enabled = {};
                    FIELD_DEFS.forEach(f => { enabled[f.key] = !!initData.fields[f.key]; });
                    setEnabledFields(enabled);
                }
            }
            if (initData.style) setStyle(prev => ({ ...prev, ...initData.style }));
            if (initData.sign_off !== undefined) {
                setSignOff(initData.sign_off);
                setCustomSignOff(!!initData.sign_off && !SIGN_OFF_OPTIONS.includes(initData.sign_off));
            }
            if (initData.photo_url) {
                setPhotoUrl(rebaseSignatureAssetUrls(initData.photo_url));
                if (initData.photo_url === '{{SenderPhoto}}' || initData.photo_url.includes('/api/public/user-photo/')) setUseProfilePhoto(true);
            }
            if (initData.logo_url) {
                setLogoUrl(rebaseSignatureAssetUrls(initData.logo_url));
                if (initData.logo_url === '{{CompanyLogo}}' || /\/api\/public\/(user|org)-logo\//.test(initData.logo_url)) setUseCompanyLogo(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prefill from user profile or admin merge vars
    useEffect(() => {
        if (!initData && !value && user && !useTokens) {
            setFields(prev => ({
                ...prev,
                name: user.name || '',
                title: user.title || '',
                email: user.email || '',
                phone: user.phone || '',
                company: user.company_name || '',
                website: user.personal_website || user.company_website || '',
                linkedin: user.linkedin_url || '',
                pronouns: user.pronouns || '',
            }));
            // #970: the permanent proxy URL, never the 1-hour presigned `user.picture`.
            const photo = senderAssetUrl(user, '{{SenderPhoto}}');
            const logo = senderAssetUrl(user, '{{CompanyLogo}}');
            if (photo) { setPhotoUrl(photo); setUseProfilePhoto(true); }
            if (logo) { setLogoUrl(logo); setUseCompanyLogo(true); }
        }
        if (!initData && !value && useTokens) {
            setFields({
                name: '{{SenderName}}',
                title: '{{SenderTitle}}',
                company: '{{SenderCompany}}',
                email: '{{SenderEmail}}',
                phone: '{{SenderPhone}}',
                website: '',
                linkedin: '{{SenderLinkedIn}}',
                address: '{{SenderAddress}}',
                pronouns: '',
                calendar_link: '',
                tagline: '',
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Build HTML + text whenever anything changes. An image link that fails the
    // link check (#970) never reaches the HTML: it is flagged in the form instead.
    // #970: with merge fields, a blank sender field builds as its token, so it
    // fills from each sender's profile as the Fields help text promises. The
    // saved `fields` keep what was typed.
    const builtFields = useMemo(() => (useTokens ? withSenderTokens(fields) : fields), [useTokens, fields]);

    const htmlOutput = useMemo(() =>
        buildSignatureHtml({
            layout, fields: builtFields, enabledFields, fieldOrder, style, photoShape, signOff,
            photoUrl: normalizeImageLink(photoUrl) || '',
            logoUrl: normalizeImageLink(logoUrl) || '',
        }),
        [layout, builtFields, enabledFields, fieldOrder, style, photoUrl, logoUrl, photoShape, signOff]
    );

    const textOutput = useMemo(() =>
        buildSignatureText({ fields: builtFields, enabledFields, signOff }),
        [builtFields, enabledFields, signOff]
    );

    // When preview user changes, build resolved preview
    const previewHtmlOutput = useMemo(() => {
        // Merge-field mode outside admin: preview as the signed-in user.
        const previewAs = isAdmin ? previewUser : (mergeFields ? user : null);
        if (!previewAs) return null;
        // Resolve exactly as the composers do (lib/signatureMergeVars.js), so the
        // preview is what that sender's recipients will see — photo/logo included,
        // through the permanent proxy URLs (v2.16.38). #955: this replaced a local
        // map whose `{{…}}` keys applyMerge wrapped a second time, so the admin
        // "preview as" never substituted anything.
        const name = previewAs.name || `${previewAs.first_name || ''} ${previewAs.last_name || ''}`.trim() || previewAs.email || '';
        return resolveSigVars(htmlOutput || '', { ...previewAs, name }, previewAs.email || '');
    }, [isAdmin, mergeFields, user, previewUser, htmlOutput]);

    // Emit changes. #970: on every change to the stored data, not only to the
    // HTML: editing or reordering a field that is off changes no HTML, but it is
    // still part of what gets saved.
    useEffect(() => {
        const data = {
            layout,
            photo_shape: photoShape,
            fields,
            style,
            // Saved as it goes into the HTML (https:// added, /api/ made absolute);
            // an invalid link is kept as typed so the form can show it, and Save is blocked.
            photo_url: normalizeImageLink(photoUrl) ?? photoUrl,
            logo_url: normalizeImageLink(logoUrl) ?? logoUrl,
            enabled_fields: enabledFields,
            field_order: fieldOrder,
            sign_off: signOff,
        };
        onChange?.(htmlOutput);
        onDataChange?.(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlOutput, layout, photoShape, fields, style, photoUrl, logoUrl, enabledFields, fieldOrder, signOff]);

    // Validity per tab (#970). The dialog uses it to block Save and mark the tab.
    const photoLinkBad = !useProfilePhoto && !isValidImageLink(photoUrl);
    const logoLinkBad = !useCompanyLogo && !isValidImageLink(logoUrl);
    const nameSizeBad = !sizeInRange('name_size', style.name_size);
    const photoSizeBad = !sizeInRange('photo_size', style.photo_size);
    const logoSizeBad = !sizeInRange('logo_size', style.logo_size);
    const styleInvalid = nameSizeBad;
    const photosInvalid = photoLinkBad || logoLinkBad || photoSizeBad || logoSizeBad;
    useEffect(() => {
        onValidityChange?.({ style: styleInvalid, photos: photosInvalid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [styleInvalid, photosInvalid]);

    const updateField = (key, val) => setFields(prev => ({ ...prev, [key]: val }));
    const updateStyle = (key, val) => setStyle(prev => ({ ...prev, [key]: val }));
    const toggleField = (key) => setEnabledFields(prev => ({ ...prev, [key]: !prev[key] }));
    const moveField = (key, delta) => setFieldOrder(prev => {
        const i = prev.indexOf(key);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= prev.length) return prev;
        return arrayMove(prev, i, j);
    });

    const applyPreset = (preset) => {
        setStyle(prev => ({
            ...prev,
            accent_color: preset.accent,
            text_color: preset.text,
            divider_color: preset.divider,
            name_size: preset.nameSize,
        }));
    };
    const activePreset = PRESETS.find(p =>
        p.accent === style.accent_color && p.text === style.text_color && p.divider === style.divider_color
    );

    const setUsePhoto = (on) => {
        setUseProfilePhoto(on);
        setPhotoUrl(on ? (useTokens ? '{{SenderPhoto}}' : senderAssetUrl(user, '{{SenderPhoto}}')) : '');
    };
    const setUseLogo = (on) => {
        setUseCompanyLogo(on);
        setLogoUrl(on ? (useTokens ? '{{CompanyLogo}}' : senderAssetUrl(user, '{{CompanyLogo}}')) : '');
    };

    const signOffSelectValue = customSignOff ? '__custom__' : (signOff ? signOff : '__none__');
    const onSignOffSelect = (v) => {
        if (v === '__none__') { setCustomSignOff(false); setSignOff(''); }
        else if (v === '__custom__') { setCustomSignOff(true); }
        else { setCustomSignOff(false); setSignOff(v); }
    };

    // DnD handler for field reorder
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setFieldOrder(prev => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
        }
    };

    const enabledCount = Object.values(enabledFields).filter(Boolean).length;
    const allOn = FIELD_DEFS.every(f => enabledFields[f.key]);
    const orderedFieldDefs = fieldOrder.map(key => FIELD_DEFS.find(f => f.key === key)).filter(Boolean);

    const tabs = [
        ...(generalTab ? [{ id: 'general', label: 'General', hasError: !!generalTab.hasError }] : []),
        { id: 'style', label: 'Layout & Style', hasError: styleInvalid },
        { id: 'photos', label: 'Photos', hasError: photosInvalid },
        { id: 'fields', label: 'Fields', hasError: false },
    ];

    // Template variables (admin): a reference list, shown in every editor mode.
    const templateVars = [...FIELD_DEFS.filter(f => f.mergeVar).map(f => f.mergeVar), '{{SenderPhoto}}', '{{CompanyLogo}}'];

    const preview = (
        <aside aria-label="Live preview" className="flex min-h-0 flex-col gap-3 overflow-y-auto border-t bg-muted/40 p-5 md:border-l md:border-t-0">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">{isAdmin ? 'Live preview' : 'Live preview · as you'}</h3>
                {isAdmin && (
                    <UserPreviewSearch
                        users={previewUsers}
                        selectedId={previewUserId}
                        selectedUser={previewUser}
                        onSelect={(uid) => {
                            setPreviewUserId(uid);
                            if (!uid) { setPreviewUser(null); return; }
                            setPreviewUser(previewUsers.find(u => u.user_id === uid) || null);
                        }}
                    />
                )}
            </div>
            <div className="rounded-lg border bg-white p-5 shadow-sm">
                <div className="mb-4 space-y-2" aria-hidden="true">
                    <div className="h-2.5 w-4/5 rounded bg-stone-100" />
                    <div className="h-2.5 w-3/5 rounded bg-stone-100" />
                </div>
                <div
                    className="signature-preview"
                    data-testid="signature-preview"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                            previewHtmlOutput || htmlOutput,
                            { ADD_TAGS: ['style'], ADD_ATTR: ['target'] }
                        )
                    }}
                />
            </div>
            <p className="flex gap-2 text-xs text-muted-foreground">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {isAdmin && !previewUser
                    ? 'Merge variables fill in for each sender. Pick a user to preview as them.'
                    : 'Same layout, colours and fields the composer inserts.'}
            </p>
            {isAdmin && (
                <div className="mt-auto space-y-2 border-t pt-3" data-testid="signature-template-vars">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold">Template variables</span>
                        <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold">Admin only</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {templateVars.map(v => (
                            <span key={v} className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-violet-700">
                                {v}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </aside>
    );

    // #970 QA: the controls must fit a 1366x768 window without scrolling, so
    // the preview column is narrower and every panel below is laid out compact.
    const shell = 'grid h-full min-h-0 grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(300px,360px)]';

    if (viewMode !== 'visual') {
        const isHtml = viewMode === 'html';
        return (
            <div className={shell}>
                <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-6">
                    <div className="flex items-center gap-2">
                        <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-semibold">Read-only</span>
                        <p className="text-sm text-muted-foreground">
                            {isHtml
                                ? 'The HTML this signature sends. To change it, use the Visual tab.'
                                : 'Plain-text version for email clients that don\'t show HTML. To change it, use the Visual tab.'}
                        </p>
                    </div>
                    <textarea
                        value={isHtml ? htmlOutput : textOutput}
                        readOnly
                        aria-label={isHtml ? 'Signature HTML (read-only)' : 'Signature text (read-only)'}
                        data-testid={isHtml ? 'signature-html-output' : 'signature-text-output'}
                        onClick={(e) => e.target.select()}
                        className={cn(
                            'min-h-[360px] w-full flex-1 resize-none rounded-md border px-3 py-2 font-mono focus-visible:outline-none',
                            isHtml ? 'border-stone-700 bg-stone-900 text-xs text-green-400' : 'bg-muted/40 text-sm'
                        )}
                    />
                </div>
                {preview}
            </div>
        );
    }

    return (
        <Tabs value={tab} onValueChange={setTab} className={shell}>
            <div className="flex min-h-0 flex-col">
                <div className="shrink-0 px-5 pt-4">
                    <TabsList aria-label="Signature builder sections">
                        {tabs.map(t => (
                            <TabsTrigger key={t.id} value={t.id} data-testid={`signature-tab-${t.id}`}>
                                {t.label}
                                {t.hasError && (
                                    <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-destructive" aria-label="has errors" data-testid={`signature-tab-${t.id}-error`} />
                                )}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-3 pt-3" data-testid="signature-controls-scroll">
                    {generalTab && (
                        <TabsContent value="general" forceMount className="mt-0 data-[state=inactive]:hidden">
                            <Card className="shadow-sm"><CardContent className="p-4">{generalTab.content}</CardContent></Card>
                        </TabsContent>
                    )}

                    {/* Layout & Style */}
                    <TabsContent value="style" forceMount className="mt-0 data-[state=inactive]:hidden" data-testid="signature-panel-style">
                        <Card className="shadow-sm">
                            <CardContent className="space-y-4 p-4">
                                <div className="space-y-2">
                                    <Label id="sig-layout-label">Layout</Label>
                                    <div role="radiogroup" aria-labelledby="sig-layout-label" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                        {LAYOUTS.map(l => (
                                            <button key={l.id} type="button" role="radio" aria-checked={layout === l.id}
                                                onClick={() => setLayout(l.id)}
                                                className={cn(
                                                    'flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                                                    layout === l.id ? 'border-primary ring-1 ring-primary' : 'border-input'
                                                )}>
                                                <l.icon className="h-4 w-4 shrink-0" />
                                                {l.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label id="sig-preset-label">Colour preset</Label>
                                    <div role="group" aria-labelledby="sig-preset-label" className="flex flex-wrap gap-1.5">
                                        {PRESETS.map(p => (
                                            <Button key={p.id} type="button" variant="outline" size="sm"
                                                aria-pressed={activePreset?.id === p.id}
                                                onClick={() => applyPreset(p)}
                                                className={cn('gap-1.5', activePreset?.id === p.id && 'border-primary bg-accent')}>
                                                <span className="h-3 w-3 rounded-full border" style={{ background: p.accent }} />
                                                {p.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
                                    <ColorInput id="sig-accent" label="Accent colour" value={style.accent_color} onChange={v => updateStyle('accent_color', v)} />
                                    <ColorInput id="sig-text" label="Text colour" value={style.text_color} onChange={v => updateStyle('text_color', v)} />
                                    <ColorInput id="sig-divider" label="Divider colour" value={style.divider_color} onChange={v => updateStyle('divider_color', v)} />
                                    <div className="space-y-1.5">
                                        <Label htmlFor="sig-font" className="text-xs font-normal text-muted-foreground">Font</Label>
                                        <Select value={style.font_family} onValueChange={v => updateStyle('font_family', v)}>
                                            <SelectTrigger id="sig-font"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {FONTS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <PxInput id="sig-name-size" label="Name size" sizeKey="name_size" testId="signature-name-size"
                                        value={style.name_size} invalid={nameSizeBad}
                                        onChange={v => updateStyle('name_size', v)} />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Photos — links only (#970): no Library picker */}
                    <TabsContent value="photos" forceMount className="mt-0 data-[state=inactive]:hidden" data-testid="signature-panel-photos">
                        <Card className="shadow-sm">
                            <CardContent className="space-y-3 p-4">
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <section className="space-y-3" aria-labelledby="sig-photo-heading">
                                    <h3 id="sig-photo-heading" className="text-sm font-semibold">Profile photo</h3>
                                    <div className="flex items-center gap-3">
                                        <Switch id="sig-use-photo" checked={useProfilePhoto} onCheckedChange={setUsePhoto} data-testid="signature-use-photo" />
                                        <Label htmlFor="sig-use-photo">{useTokens ? 'Use sender photo' : 'Use profile photo'}</Label>
                                    </div>
                                    {useProfilePhoto ? (
                                        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                                            {useTokens
                                                ? <>Each sender&apos;s own profile photo <code className="font-mono">{'{{SenderPhoto}}'}</code></>
                                                : <>Your profile photo, saved as a permanent link{photoUrl ? '' : '. Upload one in Settings › Profile first.'}</>}
                                        </p>
                                    ) : (
                                        <LinkInput id="sig-photo-url" label="Photo link" value={photoUrl} invalid={photoLinkBad}
                                            testId="signature-photo-url" onChange={setPhotoUrl} />
                                    )}
                                    <div className="flex flex-wrap items-start gap-3">
                                        <div className="space-y-2">
                                            <Label id="sig-shape-label">Shape</Label>
                                            <Tabs value={photoShape} onValueChange={setPhotoShape}>
                                                <TabsList aria-labelledby="sig-shape-label">
                                                    {PHOTO_SHAPES.map(s => (
                                                        <TabsTrigger key={s.id} value={s.id} data-testid={`signature-shape-${s.id}`}>{s.label}</TabsTrigger>
                                                    ))}
                                                </TabsList>
                                            </Tabs>
                                        </div>
                                        <div className="w-[120px]">
                                            <PxInput id="sig-photo-size" label="Size" sizeKey="photo_size" step={10} testId="signature-photo-size"
                                                value={style.photo_size} invalid={photoSizeBad}
                                                onChange={v => updateStyle('photo_size', v)} />
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-3" aria-labelledby="sig-logo-heading">
                                    <h3 id="sig-logo-heading" className="text-sm font-semibold">Company logo</h3>
                                    <div className="flex items-center gap-3">
                                        <Switch id="sig-use-logo" checked={useCompanyLogo} onCheckedChange={setUseLogo} data-testid="signature-use-logo" />
                                        <Label htmlFor="sig-use-logo">Use company logo</Label>
                                    </div>
                                    {useCompanyLogo ? (
                                        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                                            {useTokens
                                                ? <>Each sender&apos;s company logo <code className="font-mono">{'{{CompanyLogo}}'}</code></>
                                                : <>Your company logo, saved as a permanent link{logoUrl ? '' : '. Add one in Settings › Profile first.'}</>}
                                        </p>
                                    ) : (
                                        <LinkInput id="sig-logo-url" label="Logo link" value={logoUrl} invalid={logoLinkBad}
                                            testId="signature-logo-url" onChange={setLogoUrl} />
                                    )}
                                    <div className="w-[120px]">
                                        <PxInput id="sig-logo-size" label="Size (height)" sizeKey="logo_size" testId="signature-logo-size"
                                            value={style.logo_size} invalid={logoSizeBad}
                                            onChange={v => updateStyle('logo_size', v)} />
                                    </div>
                                </section>
                                </div>

                                <p className="text-xs text-muted-foreground">
                                    Links only for now. Signatures saved earlier with a library image keep showing it.
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Fields (Sign-off lives here) */}
                    <TabsContent value="fields" forceMount className="mt-0 data-[state=inactive]:hidden" data-testid="signature-panel-fields">
                        <Card className="shadow-sm">
                            <CardContent className="space-y-2 p-4">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                    <Label className="mr-auto">
                                        Signature fields{' '}
                                        <span className="font-normal text-muted-foreground">· {enabledCount} of {FIELD_DEFS.length} on · drag or use the arrows</span>
                                    </Label>
                                    <Label htmlFor="sig-signoff" className="text-xs font-normal text-muted-foreground">Sign-off</Label>
                                    <div className="w-44">
                                        <Select value={signOffSelectValue} onValueChange={onSignOffSelect}>
                                            <SelectTrigger id="sig-signoff" className="h-8" data-testid="signature-signoff"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">None</SelectItem>
                                                {SIGN_OFF_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                <SelectItem value="__custom__">Custom</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button type="button" variant="ghost" size="sm"
                                        onClick={() => {
                                            const next = {};
                                            FIELD_DEFS.forEach(f => { next[f.key] = !allOn; });
                                            setEnabledFields(next);
                                        }}>
                                        {allOn ? 'Deselect all' : 'Select all'}
                                    </Button>
                                </div>
                                {customSignOff && (
                                    <Input className="h-8 max-w-xs" value={signOff} onChange={e => setSignOff(e.target.value)}
                                        placeholder="Custom sign-off…" aria-label="Custom sign-off" />
                                )}

                                <div className="space-y-2">
                                    {/* Two columns, filled down then across, so the list keeps its reading order. */}
                                    <div className="grid gap-px overflow-hidden rounded-md border bg-border sm:grid-flow-col sm:grid-cols-2"
                                        style={{ gridTemplateRows: `repeat(${Math.ceil(orderedFieldDefs.length / 2)}, minmax(0, auto))` }}>
                                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                            <SortableContext items={fieldOrder} strategy={rectSortingStrategy}>
                                                {orderedFieldDefs.map((def, i) => (
                                                    <SortableFieldRow
                                                        key={def.key}
                                                        def={def}
                                                        enabled={!!enabledFields[def.key]}
                                                        value={fields[def.key]}
                                                        onToggle={() => toggleField(def.key)}
                                                        onChange={v => updateField(def.key, v)}
                                                        onMoveUp={i > 0 ? () => moveField(def.key, -1) : null}
                                                        onMoveDown={i < orderedFieldDefs.length - 1 ? () => moveField(def.key, 1) : null}
                                                        isAdmin={useTokens}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                        {orderedFieldDefs.length % 2 === 1 && <div className="hidden bg-background sm:block" aria-hidden="true" />}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {useTokens
                                            ? "Leave a sender field blank to fill it from each sender's profile. Fields that are off keep their text and place."
                                            : 'Fields that are off keep their text and place.'}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </div>
            </div>
            {preview}
        </Tabs>
    );
}


// -------------------------------------------------------------------
// Sortable field row (drag handle + switch + input + move buttons).
// #970: every row has its input, on or off, so a field's text can be edited
// before it is switched on, and off rows keep their text and position.
// -------------------------------------------------------------------
function SortableFieldRow({ def, enabled, value, onToggle, onChange, onMoveUp, onMoveDown, isAdmin }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: def.key });
    const dragStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 'auto',
    };
    const Icon = def.icon;
    const placeholder = isAdmin ? (def.mergeVar || def.placeholder) : def.placeholder;

    return (
        <div
            ref={setNodeRef}
            style={dragStyle}
            data-testid={`signature-field-${def.key}`}
            className={cn(
                'flex items-center gap-1.5 px-2 py-1.5',
                enabled ? 'bg-background' : 'bg-muted text-muted-foreground',
                isDragging && 'opacity-60 shadow-lg ring-1 ring-ring'
            )}
        >
            <button type="button" {...attributes} {...listeners} aria-label={`Drag ${def.label}`}
                className="shrink-0 cursor-grab rounded p-0.5 text-muted-foreground hover:text-foreground active:cursor-grabbing">
                <GripVertical className="h-4 w-4" />
            </button>
            <Switch size="sm" checked={enabled} onCheckedChange={onToggle} aria-label={`Show ${def.label}`} data-testid={`signature-field-${def.key}-toggle`} />
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span className="w-20 shrink-0 truncate text-xs">{def.label}</span>
            {def.key === 'pronouns' ? (
                <div className="min-w-0 flex-1">
                    <PronounsPicker compact value={value} onChange={onChange} ariaLabel={def.label} />
                </div>
            ) : (
                <Input
                    className="h-7 min-w-0 flex-1 text-xs"
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    aria-label={`${def.label} text`}
                    data-testid={`signature-field-${def.key}-input`}
                />
            )}
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" disabled={!onMoveUp}
                onClick={onMoveUp || undefined} aria-label={`Move ${def.label} up`}>
                <ChevronUp className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" disabled={!onMoveDown}
                onClick={onMoveDown || undefined} aria-label={`Move ${def.label} down`}>
                <ChevronDown className="h-4 w-4" />
            </Button>
        </div>
    );
}


/* Colour swatch + hex input */
function ColorInput({ id, label, value, onChange }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-xs font-normal text-muted-foreground">{label}</Label>
            <div className="flex items-center gap-2">
                <input type="color" value={value} onChange={e => onChange(e.target.value)} aria-label={`${label} picker`}
                    className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1" />
                <Input id={id} className="font-mono text-xs" value={value} onChange={e => onChange(e.target.value)} />
            </div>
        </div>
    );
}

/* Number input with a px unit and a range message (#970: replaces the sliders) */
function PxInput({ id, label, sizeKey, value, onChange, invalid, step = 1, testId }) {
    const { min, max } = SIZE_LIMITS[sizeKey];
    const errId = `${id}-error`;
    return (
        <div className="space-y-2">
            <Label htmlFor={id}>{label}</Label>
            <div className="relative">
                <Input id={id} type="number" inputMode="numeric" min={min} max={max} step={step}
                    value={value ?? ''} onChange={e => onChange(e.target.value)}
                    aria-invalid={invalid || undefined} aria-describedby={invalid ? errId : undefined}
                    data-testid={testId}
                    className={cn('pr-10', invalid && 'border-destructive focus-visible:ring-destructive')} />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">px</span>
            </div>
            {invalid && <p id={errId} role="alert" className="text-xs text-destructive">Use {min} to {max} px.</p>}
        </div>
    );
}

/* Image link input: http(s) or a merge variable (#970) */
function LinkInput({ id, label, value, onChange, invalid, testId }) {
    const errId = `${id}-error`;
    return (
        <div className="space-y-2">
            <Label htmlFor={id}>{label}</Label>
            <Input id={id} value={value} onChange={e => onChange(e.target.value)} placeholder="https://…"
                aria-invalid={invalid || undefined} aria-describedby={invalid ? errId : undefined}
                data-testid={testId}
                className={cn(invalid && 'border-destructive focus-visible:ring-destructive')} />
            {invalid && (
                <p id={errId} role="alert" className="flex items-center gap-1.5 text-xs text-destructive" data-testid={`${testId}-error`}>
                    <Info className="h-3.5 w-3.5" /> Use a link that starts with http:// or https://
                </p>
            )}
        </div>
    );
}
